<template>
	<div class="relative !px-4 !pt-16 flex flex-col gap-4 items-center !container mx-auto !max-w-seint">
		<button
			type="button"
			aria-label="Go Back"
			@click="handleReset"
			class="inline-flex items-center absolute left-4 top-5 gap-2 text-[11px]/[14px] uppercase font-medium"
		>
			<svg-vue icon="back-arrow" />
			<span>Start Over</span>
		</button>
		<h1 class="!m-0 !p-0 font-butler-light text-3xl/[38px] font-light max-w-80 text-center">
			Here are your personalized recommendations
		</h1>
		<ul v-if="results" class="w-full flex flex-col gap-12 !m-0 !p-0">
			<li v-for="(products, key, index) in results" :key="`${index}-${key}`">
				<h4 class="text-base font-medium text-center !mb-6">{{ key }}</h4>
				<ul class="!m-0 !p-0 flex flex-col gap-5">
					<li v-for="product in products" :key="product.item_title" class="grid grid-cols-[88px_1fr] gap-4">
						<figure>
							<video
								v-if="product.item_image.mimeType?.includes('video')"
								:src="product.item_image.src"
								autoplay
								loop
								muted
								playsinline
							/>
							<img v-else :src="product.item_image.src" :alt="product.item_title" class="w-full object-contain" />
						</figure>
						<div class="flex flex-col gap-4">
							<div class="flex flex-col gap-1">
								<h5 class="!p-0 !m-0 text-sm font-medium">{{ product.item_title }}</h5>
								<p class="!p-0 !m-0 text-sm font-normal">{{ product.description }}</p>
							</div>
							<color-match-add-to-bag
								:item-sku="product.sku"
								:item-id="product.item_id"
								:price="product.item_price"
								:out-of-stock="product.oos"
							/>
						</div>
					</li>
					<template v-if="!key.includes('Collections')">
						<color-match-bulk-add-to-bag :items="products" />
					</template>
				</ul>
			</li>
		</ul>
	</div>
	<section class="bg-utility-gray-100 !mt-8 flex flex-col gap-8 items-center !pt-6 !px-4 !pb-10 w-full">
		<div>
			<h1 class="!m-0 !p-0 font-butler-light text-3xl/[38px] font-light">Save your results?</h1>
			<p class="!m-0 !p-0 text-sm text-center">We'll email your results</p>
		</div>
		<div class="flex flex-col gap-2 w-full max-w-[400px]">
			<input
				name="fullName"
				type="text"
				placeholder="Full name"
				v-model="fullName"
				class="!h-11 placeholder:text-placeholder text-sm bg-primary_no-hover !seint-border border-brand-solid focus:border-brand-solid focus:!ring-0 focus:outline-none"
			/>
			<input
				name="email"
				type="email"
				placeholder="Email address"
				v-model="email"
				@input="validateEmail"
				class="!h-11 placeholder:text-placeholder !m-0 text-sm bg-primary_no-hover !seint-border border-brand-solid focus:border-brand-solid focus:!ring-0 focus:outline-none"
			/>
			<div v-if="showShareWithAffiliate" class="inline-flex items-center gap-x-2 !mb-2">
				<label class="relative flex items-center rounded-none cursor-pointer" for="shareWithAffiliate">
					<input
						type="checkbox"
						id="shareWithAffiliate"
						name="shareWithAffiliate"
						v-model="shareWithAffiliate"
						class="!mr-0 before:content[''] text-black peer relative h-5 w-5 cursor-pointer !appearance-none rounded-none border border-secondary transition-all before:absolute before:top-1/2 before:left-1/2 before:block before:h-7 before:w-7 before:-translate-y-1/2 before:-translate-x-1/2 before:rounded-none before:bg-black before:opacity-0 before:transition-opacity checked:border-black checked:bg-black checked:before:bg-black hover:before:opacity-10 focus:!border-black focus:!shadow-none focus:outline-none focus:ring-offset-0 focus:ring-transparent focus:!rounded-none focus:!text-black"
					/>
				</label>
				<label for="shareWithAffiliate" class="cursor-pointer select-none"> Share with {{ affiliateName }} </label>
			</div>
			<button
				type="button"
				:disabled="disabled || loading"
				aria-label="Next"
				@click="handleSendResults"
				class="uppercase btn-primary-border btn-primary-fg btn-primary-bg !py-4 w-full text-[11px]/[14px] border disabled:border-disabled_sublte disabled:bg-disabled disabled:fg-disabled"
			>
				{{ loading ? 'Sending' : 'Submit' }}
			</button>
		</div>
		<p v-if="affiliateEmail" class="text-xs/[18px] text-center max-w-[370px]">
			Have questions? Contact {{ affiliateName }}
			<a :href="affiliateEmail" aria-label="Contact Your Artist" class="underline">here</a>
		</p>
		<p class="text-xs/[18px] text-center max-w-[370px]">
			By entering your email address, you agree to our
			<a :href="privacyRoute" target="_blank" class="underline">Privacy Policy</a>.
		</p>
	</section>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'

const props = defineProps({
	privacyRoute: {
		type: String,
		required: true,
	},
	initialFullName: {
		type: String,
		default: null,
	},
	initialEmail: {
		type: String,
		default: null,
	},
	results: {
		type: Object,
		default: null,
	},
	sendResults: {
		type: Function,
		required: true,
	},
	reset: {
		type: Function,
		required: true,
	},
	affiliate: {
		type: Object,
		default: null,
	},
})

const loading = ref(false)
const fullName = ref(props.initialFullName)
const email = ref(props.initialEmail)
const emailValid = ref(false)
const showShareWithAffiliate = ref(!!props.affiliate)
const shareWithAffiliate = ref(false)

onMounted(() => {
	validateEmail()
	window.scrollTo(0, 0)
})

const disabled = computed(() => {
	const isFullNameReady = !!fullName.value
	const isEmailReady = !!email.value
	const isEmailValid = emailValid.value
	return ![isFullNameReady, isEmailReady, isEmailValid].every(Boolean)
})
const affiliateName = computed(() => {
	if (showShareWithAffiliate.value) {
		return `Artist Affiliate - ${props.affiliate.firstName} ${props.affiliate.lastName}`
	}
	return null
})
const affiliateEmail = computed(() => {
	if (showShareWithAffiliate.value) {
		return `mailto:${props.affiliate.email}`
	}
	return null
})

const validateEmail = () => {
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	emailValid.value = emailPattern.test(email.value)
}
const handleSendResults = async () => {
	loading.value = true
	props.sendResults({
		fullName: fullName.value,
		email: email.value,
		sendEmail: true,
		shareWithAffiliate: shareWithAffiliate.value,
		affiliate: props.affiliate,
	})
	setTimeout(() => {
		loading.value = false
		fullName.value = null
		email.value = null
		shareWithAffiliate.value = false
	}, 5000)
}
const handleReset = () => props.reset()
</script>
